import i18next, { TFunction } from 'i18next'
import { ZodError, ZodRawShape, z } from 'zod'
import { ZodRequired, errorMap, requiredFieldError } from './Components/zodErrorMap'

export enum AssetType {
	// homes
	House = 'house',
	Apartment = 'apartment',
	VacationHome = 'vacationHome',
	AllotmentGardenHouse = 'allotmentGardenHouse',
	Farmhouse = 'farmhouse',
	BuildingPlot = 'buildingPlot',
	Houseboat = 'houseboat',

	// vehicles
	Car = 'car',
	Motorcycle = 'motorcycle',
	RV = 'rv',
	CampingWagon = 'campingWagon',
	Boat = 'boat',
}

export enum VehiclePropellant {
	diesel = 'diesel',
	gas = 'gas',
	electricity = 'electricity',
	hydrogen = 'hydrogen',
	none = 'none',
}

export enum VehicleFinancingType {
	OwnedAssetSpecificLoan = 'ownedAssetSpecificLoan',
	OwnedNoDebt = 'ownedNoDebt',
	OwnedOtherFinancing = 'ownedOtherFinancing',
	Leased = 'leased',
	CompanyProvided = 'companyProvided',
}

export const YearlyMileage = {
	From0To5000: 'from0To5000',
	From5000To10000: 'from5000To10000',
	From10000To15000: 'from10000To15000',
	From15000To20000: 'from15000To20000',
	From20000To25000: 'from20000To25000',
	From25000To30000: 'from25000To30000',
	From30000To35000: 'from30000To35000',
	From35000To40000: 'from35000To40000',
	From40000To45000: 'from40000To45000',
	From45000To50000: 'from45000To50000',
	From50000Plus: 'from50000Plus',
} as const

export type YearlyMileageType =
	(typeof YearlyMileage)[keyof typeof YearlyMileage]

export enum RealEstateFinancingType {
	None = 'none',
	OwnedAssetSpecificLoan = 'ownedAssetSpecificLoan',
	OwnedNoDebt = 'ownedNoDebt',
	OwnedOtherFinancing = 'ownedOtherFinancing',
	Rented = 'rented',
	Cooperative = 'cooperative',
}

z.setErrorMap(errorMap)

const asset = z.object({
	id: z.string().or(z.number()),
	description: z.string(),
	value: z.number(),
	currency: z.string(),
	deletedByApplicant: z.boolean().optional(),
	addedByApplicant: z.boolean().optional(),
	documentationLink: z.string().optional(),
	excludedFromFutureBudget: z.boolean().optional(),
})

export type AssetCommonFields = z.infer<typeof asset>

export type AssetSpecificFields = Vehicle | RealEstate

export type Asset = AssetCommonFields & AssetSpecificFields

export const overarchingTypes = {
	realEstate: [
		AssetType.House,
		AssetType.Apartment,
		AssetType.VacationHome,
		AssetType.AllotmentGardenHouse,
		AssetType.Farmhouse,
		AssetType.BuildingPlot,
		AssetType.Houseboat,
	],
	vehicle: [
		AssetType.Car,
		AssetType.Motorcycle,
		AssetType.RV,
		AssetType.Boat,
		AssetType.CampingWagon,
	],
} as const

export type OverarchingAssetType = keyof typeof overarchingTypes

/** include securities in this list to enable in flow */
export const overarchingTypeOrder: OverarchingAssetType[] = [
	'realEstate',
	'vehicle',
]

export const OverarchingAssetTypeConst = {
	RealEstate: 'realEstate',
	Vehicle: 'vehicle',
} as const

export function overarchingTypeOf(assetType: AssetType): OverarchingAssetType {
	return Object.entries(overarchingTypes).find(([, assetTypes]) =>
		(assetTypes as unknown as AssetType[]).includes(assetType),
	)?.[0] as OverarchingAssetType
}

export type RealEstateUnion = (typeof overarchingTypes.realEstate)[number]
export type VehicleUnion = (typeof overarchingTypes.vehicle)[number]

const TaxSchema = z.object({
	name: z.string(),
	amount: z.number(),
	paymentFrequency: z.string(),
})

const PeriodicTaxesSchema = z.object({
	taxes: z.array(TaxSchema),
	totalAmount: z.number(),
	paymentFrequency: z.string(),
})

const vehicle = z.object({
	assetType: z.enum(overarchingTypes.vehicle),
	registrationNumber: z.string().min(2).max(7).optional(),
	vehicleIdentificationNumber: z.string().min(17).max(17).optional(),
	propellant: z.nativeEnum(VehiclePropellant),
	year: z.number().min(1900).max(2200),
	variant: z.string().optional(),
	model: z.string().optional(),
	brand: z.string(),
	financingType: z.nativeEnum(VehicleFinancingType).optional(),
	periodicTaxes: PeriodicTaxesSchema.optional(),
	yearlyMileage: z.nativeEnum(YearlyMileage).nullable(),
	valuation: z.number().optional(),
	assetValueAddedByApplicant: z.number().optional(),
})

export type Vehicle = z.infer<typeof vehicle>

const realEstate = z.object({
	assetType: z.enum(overarchingTypes.realEstate),
	address: z.string(),
	bfeNumber: z.string().optional(),
	valuation: z.number().optional(),
	financingType: z.nativeEnum(RealEstateFinancingType).optional(),
	assetValueAddedByApplicant: z.number().optional(),
})

export type RealEstate = z.infer<typeof realEstate>

function toZodPath(error: ZodError): string {
	if ('path' in error && Array.isArray(error.path)) {
		return error.path.join('.')
	}
	return '*'
}

function handleZodErrors(
	error: any,
): Partial<Record<keyof Asset, string | ZodRequired>> | undefined {
	if (!('issues' in error) && !Array.isArray(error.issues)) {
		throw error
	}

	const zodError = error.issues as ZodError[]
	return zodError.reduce(
		(acc, next) => ({ ...acc, [toZodPath(next)]: next.message }),
		{},
	)
}

function zodValidate<T extends ZodRawShape>(
	zodObject: z.ZodObject<T>,
	input: unknown,
): Partial<Record<keyof Asset, string | ZodRequired>> | undefined {
	try {
		zodObject.parse(input)
	} catch (e) {
		return handleZodErrors(e)
	}
}

function assign<T>(t: T, v: T) {
	if (!v) {
		return t
	}
	if (!t) {
		t = {} as T
	}
	return { ...t, ...v }
}

export function validateAsset(
	input: Partial<Asset>,
): Partial<Record<keyof Asset, string | ZodRequired>> | undefined {
	if (!input.assetType) {
		return { assetType: requiredFieldError }
	}
	return assign(zodValidate(asset, input), validateSpecificFields(input))
}

export function validateSpecificFields(
	fields: Partial<AssetSpecificFields>,
):
	| Partial<Record<keyof AssetSpecificFields, string | ZodRequired>>
	| undefined {
	switch (true) {
		case overarchingTypes.realEstate.includes(
			fields.assetType as RealEstateUnion,
		):
			return zodValidate(realEstate, fields)
		case overarchingTypes.vehicle.includes(fields.assetType as VehicleUnion):
			return zodValidate(vehicle, fields)
	}
}

export function validateCommonAssetFields(
	input: Partial<Asset>,
): Partial<Record<keyof Asset, string | ZodRequired>> | undefined {
	return zodValidate(asset, input)
}

export function validateRealEstateFields(
	input: Partial<Asset>,
): Partial<Record<keyof Asset, string | ZodRequired>> | undefined {
	return zodValidate(realEstate, input)
}

export function validateVehicleFields(
	input: Partial<Asset>,
): Partial<Record<keyof Asset, string | ZodRequired>> | undefined {
	return zodValidate(vehicle, input)
}

export function joinNonEmpty(
	...parts: (string | number | undefined)[]
): string {
	return parts.filter((p) => !!p).join(' ')
}

export function translatePropellant(propellant: VehiclePropellant): string {
	switch (propellant) {
		case VehiclePropellant.diesel:
			return i18next.t('asset-propellant-type-diesel')
		case VehiclePropellant.gas:
			return i18next.t('asset-propellant-type-gas')
		case VehiclePropellant.electricity:
			return i18next.t('asset-propellant-type-electricity')
		case VehiclePropellant.hydrogen:
			return i18next.t('asset-propellant-type-hydrogen')
		case VehiclePropellant.none:
			return i18next.t('asset-propellant-type-NONE')
	}
}

export function translateFinancingType(
	financingType?: VehicleFinancingType | RealEstateFinancingType,
	assetType?: AssetType,
): string {
	if (!assetType) {
		return i18next.t('financing-type-unknown')
	}

	switch (overarchingTypeOf(assetType)) {
		case 'vehicle': {
			switch (financingType) {
				case VehicleFinancingType.OwnedOtherFinancing:
					return i18next.t('financing-type-OwnedOtherFinancing')
				case VehicleFinancingType.Leased:
					return i18next.t('vehicle-financing-type-Leased')
				case VehicleFinancingType.OwnedNoDebt:
					return i18next.t('vehicle-financing-type-OwnedNoDebt')
				case VehicleFinancingType.CompanyProvided:
					return i18next.t('vehicle-financing-type-CompanyProvided')
				case VehicleFinancingType.OwnedAssetSpecificLoan: {
					switch (assetType) {
						case AssetType.Boat:
							return i18next.t('vehicle-financing-type-boatloan')
						default:
							return i18next.t('vehicle-financing-type-carloan')
					}
				}
			}
			break
		}
		case 'realEstate': {
			switch (financingType) {
				case RealEstateFinancingType.OwnedAssetSpecificLoan:
					return i18next.t('real-estate-financing-type-Owned')
				case RealEstateFinancingType.OwnedNoDebt:
					return i18next.t('real-estate-financing-type-OwnedNoDebt')
				case RealEstateFinancingType.OwnedOtherFinancing:
					return i18next.t('real-estate-financing-type-OwnedOtherFinancing')
				case RealEstateFinancingType.Rented:
					return i18next.t('real-estate-financing-type-Rented')
				case RealEstateFinancingType.Cooperative:
					return i18next.t('real-estate-financing-type-Cooperative')
			}
		}
	}
	return i18next.t('financing-type-unknown')
}

export function translateYearlyMileage(
	yearlyMileage?: YearlyMileageType | null,
): string {
	switch (yearlyMileage) {
		case YearlyMileage.From0To5000:
			return i18next.t('vehicle-yearlyMileage-from0To5000')
		case YearlyMileage.From5000To10000:
			return i18next.t('vehicle-yearlyMileage-from5000To10000')
		case YearlyMileage.From10000To15000:
			return i18next.t('vehicle-yearlyMileage-from10000To15000')
		case YearlyMileage.From15000To20000:
			return i18next.t('vehicle-yearlyMileage-from15000To20000')
		case YearlyMileage.From20000To25000:
			return i18next.t('vehicle-yearlyMileage-from20000To25000')
		case YearlyMileage.From25000To30000:
			return i18next.t('vehicle-yearlyMileage-from25000To30000')
		case YearlyMileage.From30000To35000:
			return i18next.t('vehicle-yearlyMileage-from30000To35000')
		case YearlyMileage.From35000To40000:
			return i18next.t('vehicle-yearlyMileage-from35000To40000')
		case YearlyMileage.From40000To45000:
			return i18next.t('vehicle-yearlyMileage-from40000To45000')
		case YearlyMileage.From45000To50000:
			return i18next.t('vehicle-yearlyMileage-from45000To50000')
		case YearlyMileage.From50000Plus:
			return i18next.t('vehicle-yearlyMileage-from50000Plus')
	}

	return i18next.t('unknown')
}

function splitAddress(address: string): {
	title: string
	description: string
} {
	if (address.includes(',')) {
		const [title, ...fields] = address.split(',')
		return {
			title: title!,
			description: `${title}, ${fields.join(', ')}`,
		}
	}
	if (address.match(/\d{4}/)) {
		try {
			const [, streetPart, postalCode, city] =
				address.match(/(.*)(\d{4})(.*)/) || []
			return {
				title: streetPart!,
				description: `${streetPart}, ${[postalCode, city]
					.map((x) => x!.trim())
					.join(' ')}`,
			}
		} catch (_) {}
	}
	return { title: address, description: address }
}

export function getAssetDescription(asset: Asset): {
	title: string
	description: string
} {
	switch (true) {
		case overarchingTypes.realEstate.includes(
			asset.assetType as RealEstateUnion,
		):
			const realEstate = asset as RealEstate
			return splitAddress(realEstate.address || '')

		case overarchingTypes.vehicle.includes(asset.assetType as VehicleUnion):
			const vehicle = asset as Vehicle
			return {
				title: joinNonEmpty(vehicle.brand, vehicle.model, '', vehicle.year),
				description: joinNonEmpty(
					vehicle.registrationNumber || '',
					joinNonEmpty(
						vehicle.brand,
						vehicle.model,
						vehicle.variant,
						vehicle.year,
					),
				),
			}

		default:
			return {
				title: asset.description,
				description: asset.assetType,
			}
	}
}

export function translateOverarchingAssetType(
	overarchingType: OverarchingAssetType,
): string {
	switch (overarchingType) {
		case 'realEstate':
			return i18next.t('asset-type-real-estate')

		case 'vehicle':
			return i18next.t('asset-type-vehicle')
	}
}

export function translateAssetType(assetType: AssetType): string {
	switch (assetType) {
		case AssetType.House:
			return i18next.t('asset-type-House')
		case AssetType.Apartment:
			return i18next.t('asset-type-Apartment')
		case AssetType.VacationHome:
			return i18next.t('asset-type-VacationHome')
		case AssetType.AllotmentGardenHouse:
			return i18next.t('asset-type-AllotmentGardenHouse')
		case AssetType.Farmhouse:
			return i18next.t('asset-type-Farmhouse')
		case AssetType.BuildingPlot:
			return i18next.t('asset-type-BuildingPlot')
		case AssetType.Houseboat:
			return i18next.t('asset-type-Houseboat')
		case AssetType.Car:
			return i18next.t('asset-type-Car')
		case AssetType.Motorcycle:
			return i18next.t('asset-type-Motorcycle')
		case AssetType.RV:
			return i18next.t('asset-type-RV')
		case AssetType.CampingWagon:
			return i18next.t('asset-type-Campingwagon')
		case AssetType.Boat:
			return i18next.t('asset-type-Boat')
	}
}

export function groupByOverarchingAssetType(
	assets: Asset[],
): Record<OverarchingAssetType, { title: string; assets: Asset[] }> {
	const groups: Record<
		OverarchingAssetType,
		{ title: string; assets: Asset[] }
	> = Object.keys(overarchingTypes).reduce(
		(acc, type) => {
			const ot = type as OverarchingAssetType
			acc[ot] = {
				title: translateOverarchingAssetType(ot),
				assets: [],
			}
			return acc
		},
		{} as Record<OverarchingAssetType, { title: string; assets: Asset[] }>,
	)
	for (const asset of assets) {
		groups[overarchingTypeOf(asset.assetType)].assets.push(asset)
	}
	return groups
}
